var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineComponent as _defineComponent } from 'vue';
import { createVNode as _createVNode, unref as _unref, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue";
import ViewActivityLog from '@/components/ActivityLog/ViewActivityLog.vue';
import { activityLogFilterValues } from '@/helper/helper';
import User from '@/models/user';
import { setupVueI18n } from '@/plugin/vue-i18n';
import { CalendarEventService } from '@/service/CalendarEventService';
import { UserService } from '@/service/UserService';
import AppState from '@/states/view_appointment';
import { UIContentWrap } from '@gohighlevel/ghl-ui';
import '@gohighlevel/ghl-ui/dist/style.css';
import moment from 'moment-timezone';
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import '../index.css';
export default /*#__PURE__*/ _defineComponent({
    __name: 'ViewActivityLog',
    setup(__props) {
        setupVueI18n();
        const { t } = useI18n();
        const isLoading = ref(false);
        const activityTimeLine = ref({});
        const timeFormat = 'ddd, MMM Do, YYYY | h:mm a';
        const range = ref([
            moment().subtract(3, 'months').toDate(),
            moment().toDate(),
        ]);
        const fieldNameMap = {
            appointmentStatus: t('activity_log.appointment_status'),
            assignedUserId: t('activity_log.assigned_user'),
        };
        onMounted(() => {
            fetchActivityLogs();
        });
        // Function to fetch activity logs
        const fetchActivityLogs = () => __awaiter(this, void 0, void 0, function* () {
            isLoading.value = true;
            activityTimeLine.value = [];
            // Setting up the parameters for the API request.
            const params = {
                id: AppState.eventId,
                locationId: AppState.locationId,
                limit: 50,
                paginateType: 'first',
                startAt: range.value[0],
                endAt: range.value[1],
                ignoreWhiteList: true,
                includeInternal: true,
            };
            try {
                // Making the API request to get activity logs for the specified parameters.
                const { data } = yield CalendarEventService.getActivityLog(params);
                const activityLogData = yield Promise.all(data.logs.map((log) => __awaiter(this, void 0, void 0, function* () {
                    var _a, _b, _c, _d, _e, _f;
                    // Initialize variables to store user and assigned user data.
                    let user, assignedUser;
                    // If the log has a sourceId, try to get user details for that sourceId.
                    if (log.source === 'web_app' && log.sourceName && log.sourceId) {
                        try {
                            const { data: userData } = yield UserService.getUserDetails(log.sourceId);
                            user = new User(userData);
                        }
                        catch (error) {
                            /* empty */
                        }
                    }
                    // If the log contains 'assignedUserId' field and it is updated, get assigned user details.
                    if (log.changedFields.includes('assignedUserId') &&
                        ((_a = log === null || log === void 0 ? void 0 : log.after) === null || _a === void 0 ? void 0 : _a.assignedUserId)) {
                        const { data: assignedUserData } = yield UserService.getUserDetails((_b = log === null || log === void 0 ? void 0 : log.after) === null || _b === void 0 ? void 0 : _b.assignedUserId);
                        assignedUser = new User(assignedUserData);
                        // Update the 'assignedUserId' field in the log with the assigned user's name.
                        if (assignedUser.name) {
                            log.after.assignedUserId = assignedUser.name;
                        }
                    }
                    // Formatting start and end time in required format
                    if ((_c = log.after) === null || _c === void 0 ? void 0 : _c.startTime) {
                        log.after.startTime = moment(log.after.startTime)
                            .tz(AppState.timezone)
                            .format(timeFormat);
                    }
                    if ((_d = log.after) === null || _d === void 0 ? void 0 : _d.endTime) {
                        log.after.endTime = moment(log.after.endTime)
                            .tz(AppState.timezone)
                            .format(timeFormat);
                    }
                    // Initialize an object to store the processed timeline data for this log.
                    let timelineData = {};
                    if (log.type === 'CREATED') {
                        // Handle the different scenarios when the log is of type 'CREATED'.
                        // If the type if ggole or outlook set the data accordingly
                        if (log.changedFields.includes('google') ||
                            log.changedFields.includes('outlook')) {
                            // Log indicates data pulled from Google or Outlook.
                            timelineData = {
                                changeLabel: `Pulled from ${log.changedFields.includes('google') ? 'Google' : 'Outlook'}`,
                                createdAt: log.createdAt,
                                from: null,
                                to: null,
                                source: log.subSource || log.source,
                                fields: log.changedFields,
                                type: 'download',
                                currentValues: log.after,
                            };
                        }
                        else {
                            timelineData = {
                                changeLabel: 'Created',
                                createdAt: log.createdAt,
                                from: null,
                                to: null,
                                source: log.subSource || log.source,
                                userName: user === null || user === void 0 ? void 0 : user.name,
                                fields: log.changedFields,
                                currentValues: log.after,
                            };
                        }
                    }
                    else if (log.type === 'UPDATED') {
                        // Filter the changed fields and handle 'google' or 'outlook' fields separately for upload cases.
                        let filteredChangedFields = log.changedFields.filter(field => { var _a; return activityLogFilterValues.includes(field) && ((_a = log.after) === null || _a === void 0 ? void 0 : _a[field]); });
                        const uploadField = filteredChangedFields.find(field => field === 'google' || field === 'outlook');
                        filteredChangedFields = filteredChangedFields.filter(field => field !== uploadField);
                        if (uploadField) {
                            timelineData = {
                                changeLabel: `${t('activity_log.uploaded_to')} ${uploadField}`,
                                createdAt: log.createdAt,
                                from: '',
                                to: '',
                                source: log.subSource || log.source,
                                fields: [uploadField],
                                type: 'upload',
                                currentValues: log.after,
                            };
                        }
                        // Check for specific 'appoinmentStatus' and set the changeLabel accordingly for cancelled cases.
                        if (((_e = log.after) === null || _e === void 0 ? void 0 : _e.appoinmentStatus) === 'cancelled') {
                            timelineData = {
                                changeLabel: t('activity_log.cancelled'),
                                createdAt: log.createdAt,
                                from: '',
                                to: '',
                                source: log.subSource || log.source,
                                fields: [],
                                type: 'cancelled',
                                currentValues: log.after,
                            };
                        }
                        // If only one field is changed, set the changeLabel accordingly.
                        if (filteredChangedFields.length === 1) {
                            timelineData = {
                                changeLabel: `${fieldNameMap[filteredChangedFields[0]] ||
                                    filteredChangedFields[0]} ${t('activity_log.edited')}`,
                                createdAt: log.createdAt,
                                from: null,
                                to: (_f = log.after) === null || _f === void 0 ? void 0 : _f[filteredChangedFields[0]],
                                source: log.subSource || log.source,
                                userName: user === null || user === void 0 ? void 0 : user.name,
                                fields: [filteredChangedFields[0]],
                                currentValues: log.after,
                            };
                        }
                        else if (filteredChangedFields.length > 1) {
                            // If multiple fields are changed, create a summarized changeLabel.
                            let changeLabel = '';
                            filteredChangedFields
                                .filter((field, ind) => ind < 2)
                                .forEach(field => {
                                changeLabel += `${field}, `;
                            });
                            changeLabel = changeLabel.slice(0, -2);
                            changeLabel +=
                                filteredChangedFields.length > 2
                                    ? `... + ${filteredChangedFields.length - 2} ${t('activity_log.other_edits')}`
                                    : ` ${t('activity_log.edited')}`;
                            timelineData = {
                                changeLabel,
                                createdAt: log.createdAt,
                                from: null,
                                to: null,
                                source: log.subSource || log.source,
                                userName: user === null || user === void 0 ? void 0 : user.name,
                                fields: filteredChangedFields,
                                currentValues: log.after,
                            };
                        }
                    }
                    return timelineData;
                })));
                isLoading.value = false;
                // Filter out empty sections and sort the activity timeline data by createdAt date in descending order.
                activityTimeLine.value = activityLogData
                    .filter(section => Object.keys(section).length > 0) // Filter out empty sections
                    .sort((a, b) => moment(b.createdAt).diff(moment(a.createdAt)));
                // Filter the activity timeline based on date labels such as 'Today', 'Yesterday', etc.
                filterActivityTimelineBasedOnDate();
            }
            catch (error) {
                isLoading.value = false;
            }
        });
        const filterActivityTimelineBasedOnDate = () => {
            const today = moment().format('YYYY-MM-DD');
            const yesterday = moment().subtract(1, 'days').format('YYYY-MM-DD');
            // Reduce and group the activity timeline data based on date labels.
            activityTimeLine.value = activityTimeLine.value.reduce((acc, log) => {
                const logDate = moment(log.createdAt).format('YYYY-MM-DD');
                let dateLabel;
                if (logDate === today) {
                    dateLabel = 'Today';
                }
                else if (logDate === yesterday) {
                    dateLabel = 'Yesterday';
                }
                else {
                    dateLabel = logDate;
                }
                // Add the log to the corresponding date label group.
                if (!acc[dateLabel]) {
                    acc[dateLabel] = [];
                }
                acc[dateLabel].push(log);
                return acc;
            }, {});
        };
        // Watch for changes in the 'range' variable and trigger the fetchActivityLogs function.
        watch(() => range.value, fetchActivityLogs);
        // Function to handle date change, which sets the 'range' variable to the new value.
        const onDateChange = value => {
            const formattedDates = value.map(timestamp => moment(timestamp).toDate());
            range.value = formattedDates;
        };
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_unref(UIContentWrap), null, {
                default: _withCtx(() => [
                    _createVNode(ViewActivityLog, {
                        "is-loading": isLoading.value,
                        range: range.value,
                        "on-date-change": onDateChange,
                        "activity-time-line": activityTimeLine.value
                    }, null, 8, ["is-loading", "range", "activity-time-line"])
                ]),
                _: 1
            }));
        };
    }
});
